import React, { useState, useMemo, useEffect } from "react";
import { ThemeProvider, createTheme, CssBaseline, Box } from "@mui/material";
import Loader from "./components/Loader";
import CustomCursor from "./components/CustomCursor";
import ParticlesBackground from "./components/ParticlesBackground";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "@studio-freight/lenis";
import Navbar from "./components/Navbar";
import Home from "./components/Home";
import Footer from "./components/Footer";

const getAppTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: mode === "dark" ? "#00E5FF" : "#00B8D4",
        light: "#33EAFF",
        dark: "#00A0B2",
      },
      secondary: {
        main: "#7C4DFF",
        light: "#9670FF",
        dark: "#5635B2",
      },
      background: {
        default: mode === "dark" ? "#0A192F" : "#F8FAFC",
        paper: mode === "dark" ? "rgba(16, 28, 50, 0.8)" : "rgba(255, 255, 255, 0.8)",
      },
      text: {
        primary: mode === "dark" ? "#F1F5F9" : "#1E293B",
        secondary: mode === "dark" ? "#94A3B8" : "#475569",
      },
    },
    typography: {
      fontFamily: '"Outfit", "Inter", "Poppins", sans-serif',
      h1: {
        fontWeight: 800,
        letterSpacing: "-0.02em",
      },
      h2: {
        fontWeight: 700,
        letterSpacing: "-0.01em",
      },
      h3: {
        fontWeight: 700,
      },
      button: {
        fontWeight: 600,
        textTransform: "none",
      },
    },
    shape: {
      borderRadius: 16,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            cursor: 'none',
            scrollbarWidth: "thin",
            scrollbarColor: mode === "dark" ? "#00E5FF #0A192F" : "#00B8D4 #F8FAFC",
            "&::-webkit-scrollbar": {
              width: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: mode === "dark" ? "#0A192F" : "#F8FAFC",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "dark" ? "#1E293B" : "#CBD5E1",
              borderRadius: "20px",
              border: mode === "dark" ? "2px solid #0A192F" : "2px solid #F8FAFC",
              transition: "background-color 0.3s ease",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: mode === "dark" ? "#00E5FF" : "#00B8D4",
            },
          },
          'a, button': {
            cursor: 'none !important',
          }
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: "10px 24px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: mode === "dark" 
                ? "0 4px 20px rgba(0, 229, 255, 0.4)" 
                : "0 4px 20px rgba(0, 184, 212, 0.3)",
            },
          },
          containedPrimary: {
            background: "linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #33EAFF 0%, #9670FF 100%)",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "dark" ? "rgba(16, 28, 50, 0.5)" : "rgba(255, 255, 255, 0.7)",
            backdropFilter: "blur(12px)",
            border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
            borderRadius: 20,
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-8px)",
              boxShadow: mode === "dark"
                ? "0 12px 24px rgba(0, 0, 0, 0.4)"
                : "0 12px 24px rgba(0, 0, 0, 0.1)",
              borderColor: mode === "dark" ? "rgba(0, 229, 255, 0.3)" : "rgba(0, 184, 212, 0.3)",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 20,
          },
        },
      },
    },
  });

const App = () => {
  const [mode, setMode] = useState("dark");
  const [isLoading, setIsLoading] = useState(true);
  const theme = useMemo(() => getAppTheme(mode), [mode]);

  useEffect(() => {
    if (isLoading) return;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Update lenis on window resize
    const handleResize = () => {
      lenis.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      lenis.destroy();
      window.removeEventListener('resize', handleResize);
    };
  }, [isLoading]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "dark" ? "light" : "dark"));
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AnimatePresence mode="wait">
        {isLoading ? (
          <Loader key="loader" finishLoading={() => setIsLoading(false)} />
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <ParticlesBackground />
            <CustomCursor />
            <Navbar toggleTheme={toggleTheme}/>
            <Box component="main">
              <Home />
            </Box>
            <Footer />
          </motion.div>
        )}
      </AnimatePresence>
    </ThemeProvider>
  );
};

export default App;
